import React from 'react';
import { useNavigate } from 'react-router-dom';
import { RiPlaneLine, RiTeamLine, RiCheckboxCircleLine, RiTimeLine, RiFileList3Line, RiCalendarLine, RiFlightTakeoffLine, RiFlightLandLine, RiArrowRightLine, RiBuilding2Line, RiFileAddLine, RiAlertLine, RiInformationFill, RiAlertFill } from '@remixicon/react';
import { Card, CardHeader, CardContent, CardFooter } from '@components/ui/Card';
import { getAirportTimezone } from '@/utils/airportTimezone';
import { formatUTCToDateTime } from '@/utils';
import { getQuoteStatusConfig, getEffectiveQuoteStatus, selectAcceptedBidOption, getPaymentStatusRowConfig } from '../utils';
import { usePermissions } from '@/hooks/usePermissions';
import { MODULES, ACTIONS, ROLES, normalizeRole } from '@/lib/permissions';
import { getEffectiveAircraft } from '@/features/tails/utils';
/**
 * QuoteCard Component
 * Displays a single quote with client info, flight details, and actions
 *
 * @param {Object} props
 * @param {Object} props.quote - Quote data object
 * @param {Function} props.onViewQuote - Callback when "View Quote" is clicked
 */
const formatPaymentStatus = (status) => {
  if (!status) return '—';
  return status
    .toString()
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const QuoteCard = ({ quote, onViewQuote, onViewInvoice, hideActions = false }) => {
  const navigate = useNavigate();

  // "Build Trip Sheet" requires create + update on Booking & Quoting
  // (admin, broker, junior_broker — read-only roles excluded).
  // flight_coordinator is included because trip sheets are operations work
  // they own via the Tracking module.
  const { can, role } = usePermissions();
  const canBuildTripSheet =
    (can(MODULES.BOOKING, ACTIONS.CREATE) && can(MODULES.BOOKING, ACTIONS.UPDATE)) ||
    normalizeRole(role) === ROLES.FLIGHT_COORDINATOR;

  // Resolve data from either bid_items (quotes endpoint) or bids (client endpoint)
  const firstBid = quote.bid_items?.[0]?.bid || quote.bids?.[0] || {};
  const legs = firstBid.leg_itinerary || [];
  // Aggregate options across ALL bids on the quote, not just the first
  // bid — multi-bid trip groups (multi-leg trips with different tails
  // per leg) would otherwise undercount. Drop soft-deleted / rejected
  // rows (shortlisted:false — new REJECTED writes flip both fields in
  // lockstep, legacy rows only have the shortlisted flag) so the "N
  // tail options" count matches what the client sees in the quote
  // preview and itinerary email.
  const bidOptions = (
    (quote.bid_items || []).flatMap((bi) => bi?.bid?.bid_options || bi?.bid_options || [])
    // Fallback for the alt shape returned by the client `/my-trips` endpoint
    // (quote.bids[] instead of quote.bid_items[]).
    .concat(!quote.bid_items ? (quote.bids || []).flatMap((b) => b?.bid_options || []) : [])
  ).filter((o) => {
    // Guard on BOTH signals so a slimmed response that omits one still
    // filters correctly. `/quotes` list rows come from
    // `apiClient.getFinancialReports` — a unified endpoint that may
    // return a lighter bid_option shape than `getQuoteById`. Under
    // lockstep (SENT/DRAFT/ACCEPTED ⇔ shortlisted:true, REJECTED ⇔
    // shortlisted:false) both fields agree, so this OR is a no-op in
    // the healthy case; it saves us when only one field survives.
    if (!o) return false;
    if (o.shortlisted === false) return false;
    if (o.status === 'REJECTED') return false;
    return true;
  });

  // Once the client picks an aircraft option at checkout, that option flips
  // to status=ACCEPTED (see CardInfoForm.payQuote). It's the only status
  // write that path performs, and siblings stay untouched — so exactly one
  // ACCEPTED means "the client picked this one and paid," and the card
  // should collapse from "N tail options" to just the chosen tail.
  const acceptedOption = selectAcceptedBidOption(bidOptions);
  const acceptedAircraft = acceptedOption ? getEffectiveAircraft(acceptedOption.aircraft) : null;
  const acceptedTailLabel = acceptedAircraft?.tail_number
    || acceptedAircraft?.name
    || acceptedAircraft?.make_model
    || 'Booked aircraft';

  const excludeStatus = ["DRAFT", "SENT", "VIEWED", "SIGNED", "EXPIRED", "DECLINED", "CANCELLED"];
  const quoteStatus = quote?.status === "PAID" ? "ACCEPTED" : quote?.status;
  // Promote the raw payment_status into the badge-friendly value the card
  // renders. Rules (evaluated top-down):
  //   CANCELLED                     → REJECTED  (regardless of stage)
  //   PRE_AUTHORIZED + active stage → PAYMENT_PROCESSING
  //   WIRE_TRANSFER_IN_PROGRESS +
  //     active stage                → CAPTURED  (wire has cleared enough to accept)
  //   otherwise                     → raw payment_status
  const rawPaymentStatus = quote?.payment_status;
  const isActiveStage = !excludeStatus.includes(quoteStatus);
  const paymentStatus =
    rawPaymentStatus === "CANCELLED" ? "REJECTED" :
    isActiveStage && (rawPaymentStatus === "PRE_AUTHORIZED" || rawPaymentStatus === "WIRE_TRANSFER_IN_PROGRESS") ? "PAYMENT_PROCESSING" :
    rawPaymentStatus;

  // Route through the promotion helper so PASSENGERS_PENDING reads as
  // "Trip Sheet In Progress" once the client has submitted passenger
  // details — matches what QuoteDetailsDrawer already does. Without
  // this, the card and drawer disagree on the badge for the same quote.
  const configStatus = getQuoteStatusConfig(getEffectiveQuoteStatus(quote, quoteStatus));
  const Icon = configStatus.icon;

  const statusUpper = quoteStatus.toUpperCase();
  const needsPaidAction =
    (statusUpper === 'SENT' || statusUpper === 'VIEWED' || statusUpper === 'SIGNED') &&
    (paymentStatus || '').toUpperCase() === 'CAPTURED';

  const handleViewClick = () => {
    if (onViewQuote) {
      onViewQuote(quote, quoteStatus, paymentStatus);
    } else {
      navigate(`/quotes/${quote.id}`);
    }
  };

  // PASSENGERS_PENDING and WAITING_FLIGHT only ever exist AFTER payment
  // succeeds server-side, so the button shows unconditionally for those.
  // ACCEPTED (which quoteStatus also aliases from raw PAID) can technically
  // reach the FE without payment on data drift, so we still gate it on
  // the resolved paymentStatus being one of the in-flight / captured
  // states. CAPTURED is included so a fully-paid PASSENGERS_PENDING/
  // ACCEPTED quote doesn't hide the button.
  const showBuildTripSheet =
    (quoteStatus === 'PASSENGERS_PENDING'
    || quoteStatus === 'WAITING_FLIGHT'
    || quoteStatus === 'ACCEPTED')
        && (paymentStatus === 'WIRE_TRANSFER_IN_PROGRESS'
            || paymentStatus === 'PAYMENT_PROCESSING'
            || paymentStatus === 'ACCEPTED'
            || paymentStatus === 'CAPTURED'
            || paymentStatus === 'SETTLED_VIA_PASSPORT');

  const showPreauthWarning = excludeStatus.includes(quoteStatus) && (paymentStatus && (paymentStatus=="PRE_AUTHORIZED" || paymentStatus=="WIRE_TRANSFER_IN_PROGRESS"));

  return (
    <Card className="group hover:shadow-lg transition-all duration-200 overflow-hidden bg-white !rounded-3xl flex flex-col">
      {/* Client Info Section */}
      <CardHeader className="p-5 pb-4 !border-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="relative shrink-0">
              {quote.client?.isVerified && (
                <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center border-2 border-white">
                  <RiCheckboxCircleLine className="w-2.5 h-2.5 text-white" />
                </div>
              )}
            </div>

            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-md truncate">
                {quote.client?.first_name} {quote.client?.last_name}
              </h3>
              <p className="text-xs text-gray-700 truncate flex items-center gap-1">
                <RiBuilding2Line className="w-3 h-3 text-gray-700" />
                <span>{quote.client?.company_name || quote.client?.company || '-'}</span>
              </p>
            </div>
          </div>

          <div className="flex flex-col items-end gap-1.5 shrink-0">
            <span className={`inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-md ${configStatus.className}`}>
              {Icon && <Icon className="w-3.5 h-3.5" />}
              {configStatus.label}
            </span>
            {needsPaidAction && (
              <span
                className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-md bg-red-50 text-red-700 border border-red-200 whitespace-nowrap"
                title="Payment captured but quote is not yet marked as Paid"
              >
                <RiAlertLine className="w-3 h-3" />
                Mark as Paid
              </span>
            )}
          </div>
            
        </div>
      </CardHeader>

      <CardContent className="px-5 pt-0 pb-4 flex-1 flex flex-col">

        {/* Flight Routes */}
        <div className="space-y-2 border-t border-gray-100 pt-2">
          {/* Outbound Flight */}
          {legs.map((leg, i) => (
            <div key={`ml-${i}`} className="flex items-center justify-between border-b border-gray-100 pb-2">
              <div className="flex items-center gap-2 text-sm font-medium text-gray-900">
                <span className="bg-gray-200 flex items-center gap-1 rounded-full px-2"><RiFlightTakeoffLine className="w-4 h-4" /> {leg.departure_airport}</span>
                <RiArrowRightLine className="w-4 h-4 text-gray-400" />
                <span className="bg-gray-200 flex items-center gap-1 rounded-full px-2"><RiFlightLandLine className="w-4 h-4" />{leg.arrival_airport}</span>
              </div>
              <div className="flex items-center gap-3 text-xs text-gray-500">
                <div className="flex items-center gap-1">
                  <RiCalendarLine className="w-3.5 h-3.5" />
                  <span>{formatUTCToDateTime(leg.departure_date, getAirportTimezone(leg.departure_airport), "MMM D")}</span>
                </div>
                <div className="flex items-center gap-1">
                  <RiTimeLine className="w-3.5 h-3.5" />
                  <span>{formatUTCToDateTime(leg.departure_date, getAirportTimezone(leg.departure_airport), "HH:mm")}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Additional Info */}
        {!quote.invoice_id ? (
        <div className="text-xs text-gray-500 pt-2 pb-3 space-y-2">
          {/* Passenger count on the left; TR# (trip identifier) on the
              right of the same row — matches the invoice branch below,
              which already surfaces TR# in the same visual slot. */}
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-1.5">
              <RiTeamLine className="w-3.5 h-3.5" />
              <span>{quote.passenger_count || 0} passenger{quote.passenger_count !== 1 ? 's' : ''}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <RiTimeLine className="w-3.5 h-3.5 shrink-0" />
              <span className="font-medium text-gray-700">{quote.quote_number || '—'}</span>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <RiPlaneLine className={`w-3.5 h-3.5 ${acceptedOption ? 'text-green-600' : ''}`} />
            {acceptedOption ? (
              <span className="text-gray-700 font-medium">{acceptedTailLabel}</span>
            ) : (
              <span>{bidOptions.length} tail options</span>
            )}
          </div>
        </div>) : (
          <div className="space-y-2 pt-1">
            <div className="flex items-center justify-between text-xs text-gray-500">
              <div className="flex items-center gap-1.5">
                <RiTimeLine className="w-3.5 h-3.5 shrink-0" />
                <span className="font-medium text-gray-700">{quote.quote_number || '—'}</span>
              </div>
              {quote.invoice_type=="ADVANCE" && (
              <div className="flex items-center gap-1 text-gray-400">
                <span>Invoice not linked to a trip</span>
                <RiInformationFill className="w-3 h-3 text-gray-400" />
              </div>)}
            </div>
            {quoteStatus!=="ACCEPTED" && (
            <div className="flex items-center gap-1.5 px-3 py-2 w-full bg-amberlight text-xs rounded-md">
              <RiAlertFill className="w-3.5 h-3.5 shrink-0 text-amberdark" />
              <span className="text-gray-600">Unpaid {quote.invoice_type?.toLowerCase()} invoice</span>
            </div>)}
         
          </div>
        )}

        {/* Payment Status — shown only for accepted quotes. Row background
            + top-border tint come from paymentStatusConfig (single source
            of truth for the payment-lifecycle palette). */}
        {paymentStatus && (() => {
          const { bg, border } = getPaymentStatusRowConfig(paymentStatus);
          return (
            <div className={`flex items-center ${bg} ${border} justify-between px-3 py-2 text-sm rounded-lg mt-3`}>
              <span className="text-gray-500">Payment Status</span>
              <span className="font-medium text-gray-900">
                {getQuoteStatusConfig(paymentStatus).label}
              </span>
            </div>
          );
        })()}

        {showPreauthWarning && (() => {
          // Pre-auth holds are valid for 7 days from the invoice creation
          // (Visa/MC standard). Past that window the funds are released
          // automatically and re-authorization is required.
          const holdAnchor =
            quote?.invoice?.created_at
            || quote?.created_at;
          const holdExpiresAt = holdAnchor
            ? new Date(new Date(holdAnchor).getTime() + 7 * 24 * 60 * 60 * 1000)
            : null;
          const msLeft = holdExpiresAt ? holdExpiresAt.getTime() - Date.now() : null;
          const daysLeft = msLeft != null ? Math.max(0, Math.ceil(msLeft / (24 * 60 * 60 * 1000))) : null;
          const isHoldExpired = msLeft != null && msLeft <= 0;
          return (
            <div className="space-y-2 pt-1">
              <div className="flex items-center gap-1.5 px-3 py-2 w-full bg-amberlight text-xs rounded-md">
                <div className="flex items-center justify-between w-full text-xs text-gray-500">
                  <div className="flex items-center gap-1.5">
                    <RiAlertFill className="w-3.5 h-3.5 shrink-0 text-amberdark" />
                    <span className="text-gray-600">Action needed for confirming trip and payment.</span>
                  </div>
                  <button
                    type="button"
                    onClick={handleViewClick}
                    className="flex items-center gap-1 font-medium underline text-black shrink-0 ml-2 cursor-pointer hover:text-primary-700 transition-colors"
                  >
                    <span>Proceed</span>
                  </button>
                </div>
              </div>
              <div className="flex items-center gap-1.5 px-3 py-2 w-full border border-red-200 text-xs rounded-md">
                <div className="flex items-center justify-between w-full text-xs text-red-700">
                  <div className="flex items-center gap-1.5">
                    <RiInformationFill className="w-3.5 h-3.5 shrink-0 text-red-600" />
                    <span>
                      {holdExpiresAt
                        ? (isHoldExpired
                            ? `Payment hold expired on ${formatUTCToDateTime(holdExpiresAt.toISOString(), 'UTC', 'D MMM YYYY, HH:mm')}. Re-authorization required.`
                            : `Payment hold expires on ${formatUTCToDateTime(holdExpiresAt.toISOString(), 'UTC', 'D MMM YYYY, HH:mm')} (in ${daysLeft} day${daysLeft === 1 ? '' : 's'}).`)
                        : 'Payment hold expiry unknown — invoice/payment date missing.'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          );
        })()}

        {/* View Quote Button */}
        {!hideActions && (
        <div className="flex gap-2 mt-auto pt-3">

          {!quote.invoice_id ? (
          <button
            onClick={handleViewClick}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-accent text-primary-500 text-sm rounded-full transition-colors"
          >
            <RiFileList3Line className="w-4 h-4" />
            <span>View Quote</span>
          </button>) : (
            <button
            onClick={() => onViewInvoice?.(quote, quoteStatus, paymentStatus)}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-accent text-primary-500 text-sm rounded-full transition-colors"
          >
            <RiFileList3Line className="w-4 h-4" />
            <span>View Invoice</span>
          </button>
          )}

          {canBuildTripSheet && !quote.invoice_id && showBuildTripSheet &&
          <button
            onClick={()=> navigate(`/trip-sheet?quoteid=${quote.id}`)}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-primary-700 text-white text-sm rounded-full transition-colors"
          >
            <RiFileAddLine className="w-4 h-4" />
            <span>Build Trip Sheet</span>
          </button>
          }
        </div>
        )}
      </CardContent>
    </Card>
  );
};

export default QuoteCard;